// React imports
import React, { useEffect, useMemo, useState } from "react";

// Third-party imports
import Bugsnag from "@bugsnag/js";
import { Toast, ToastContainer } from "react-bootstrap";

// Type imports
import { AnswerListOverviewDto } from "../../../types/CompanyAnswersTypes";
import { IPepArray } from "../../PEP/PepPage";
import { KnowYourIndividual, RiskScoreDto, RiskScoreFormValues, ServiceCompanyEmployee } from "../../../types/DueDiligenceTypes";

// Config/constants imports
import { GET_KELDAN_API_URL, TOAST_DELAY } from "../../../services/config";

// Service imports
import { displayAddress, getCountryName, getTableDate, insertDash, isSSN, stripSSN } from "../../../services/utils";
import { sortEntries } from "../../PEP/DomesticSearch";

// UI component imports
import Alert, { AlertType } from "../../../ui-elements/Alert/Alert";
import Button from "../../../ui-elements/Button/Button";
import DisplayBox from "../../../ui-elements/DisplayBox/DisplayBox";
import FileUploadDropzone from "../../../ui-elements/FileUploadDropZone/FileUploadDropZone";
import InfoHeader from "../../../ui-elements/InfoHeader/InfoHeader";
import Loading from "../../../ui-elements/Loading/Loading";
import Table from "../../../ui-elements/Table/Table";

// Local component imports
import IndividualAnswers from "./Components/IndividualAnswers";
import PepWatchPEPConnectionModal from "../../PEP/PepWatchModals/PepWatchPEPConnectionModal";
import QuestionaireResults from "./Components/QuestionaireResults";
import RiskScoreModal from "../RiskScoreModal/RiskScoreModal";
import Label from "../../../ui-elements/Label/Label";
import { PepResult, Result } from "../../../types/PepTypes";
import Tooltip from "../../../ui-elements/Tooltip/Tooltip";
import GoBackLink from "../../../ui-elements/GoBackLink/GoBackLink";
import EditIndividualModal from "../EditIndividualModal";
import ForeignPepModal from "../../PEP/ForeignPepModal";
import KYCNavBar from "../../Company/KYCNavBar/KYCNavBar";
import { IKeldanApiResponse } from "../../../types/KeldanTypes";
import { AlthjodlegirValistarTooltipColumnHeader, AthaettusomRikiTooltipColumnHeader, availableVersions, QuestionListType, RenderNationalityWithTooltip, StjornmalalegTengslPEPTooltipColumnHeader } from "./Constants";
import Logs from "./Components/Logs";
import KYCSurveyProgressBar from "../../../ui-elements/KYCSurveyProgressBar/KYCSurveyProgressBar";

const ViewIndividualAnswers: React.FC<{ customerId: string; riskscoreId: string; riskscore: RiskScoreDto, answerListId: number, individual: KnowYourIndividual | null, employees: ServiceCompanyEmployee[] }> = ({
    customerId,
    riskscoreId,
    riskscore,
    answerListId,
    individual,
    employees
}) => {
    const handleBuyMembership = async (id: string) => {
        try {
            const ssn = stripSSN(id);

            if (!ssn || !isSSN(ssn)) {
                setToastShow(true);
                setToastType('error');
                setToastMessage('Ekki gild kennitala');
                return;
            }

            const requestQuery = new URLSearchParams({
                id: ssn,
                reference: '',
                address: 'true'
            });

            const url = `${GET_KELDAN_API_URL()}/Individual/BuyMembershipOnly`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });

            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                    return;
                }

                const responseBody: IKeldanApiResponse = await response.json();
                const { message } = responseBody;
                if (message) {
                    setToastShow(true);
                    setToastType('error');
                    setToastMessage(message);
                } else {
                    setToastShow(true);
                    setToastType('error');
                    setToastMessage('Villa kom upp');
                }
            } else {
                setToastShow(true);
                setToastType('error');
                setToastMessage('Villa við að sækja gögn');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            setToastShow(true);
            setToastType('error');
            setToastMessage('Netvilla kom upp');
        }
    };
    // State to hold fetched data
    // undefined - hasn't loaded yet
    // null - loading state
    // data - successfully fetched data
    const [data, setData] = useState<AnswerListOverviewDto | undefined | null | Error>(undefined);
    // Answer list index state
    const [answerListIndex, setAnswerListIndex] = useState<number>(0);
    const [stageData, setStageData] = useState<RiskScoreDto | Error | null | undefined>(undefined);

    // PEP Modal state
    const [showPepModal, setShowPepModal] = useState<{ show: boolean; data?: IPepArray[] }>({ show: false });
    // Foreign PEP Modal state
    const [foreignPepModal_isOpen, setForeignPepModal_isOpen] = useState(false);
    const [foreignPepModal_data, setForeignPepModal_data] = useState<PepResult[] | undefined>(undefined);
    // risk score state
    const [riskScore, setRiskScore] = useState<RiskScoreDto>(riskscore);
    const [riskScoreModal_isOpen, setRiskScoreModal_isOpen] = useState(false);
    const [riskScoreModal_data, setRiskScoreModal_data] = useState<RiskScoreFormValues | undefined>(undefined);
    // Send notification state
    const [disableSendNotificationButton, setDisableSendNotificationButton] = useState(!riskscore || riskscore.answered);
    const [editIndividualModal_isOpen, setEditIndividualModal_isOpen] = useState(false);

    // Create merged individual data with dateOfBirth and nationality from fetched data
    const mergedIndividual = useMemo(() => {
        if (!individual) return null;
        if (!data || data instanceof Error) return individual;

        return {
            ...individual,
            dateOfBirth: data.user?.dateOfBirth ?? individual.dateOfBirth,
            nationality: data.user?.nationality ?? individual.nationality
        };
    }, [individual, data]);

    // Toast state
    const [toastShow, setToastShow] = useState(false);
    const [toastType, setToastType] = useState<AlertType>("success");
    const [toastMessage, setToastMessage] = useState("");
    useEffect(() => {
        const fetchData = async () => {
            setData(null); // Set to loading state
            try {
                const response = await fetch(`/DueDiligence/GetIndividualsAnswers?riskscoreId=${riskscoreId}&individualId=${customerId}`);

                const data: AnswerListOverviewDto = await response.json();
                if (!response.ok) {
                    setData(undefined);
                } else {
                    setData(data);
                }
            } catch (e) {
                if (e instanceof Error) {
                    Bugsnag.notify(e);
                    setData(e);
                }
            }
        }
        fetchData();
    }, [riskscoreId, customerId]);

    useEffect(() => {
        if (answerListId && data && !(data instanceof Error)) {
            const index = data.answerLists.findIndex(answerList => answerList.answerlistId === answerListId);
            if (index !== -1) {
                setAnswerListIndex(index);
            }
        }
    }, [data, answerListId]);

    const infoHeaderData = useMemo(() => {
        if ((individual === null && data === undefined) || data instanceof Error) {
            return [];
        }
        const infoHeaderData = [{
            title: 'Nafn',
            value: individual ? individual.name : data === null ? <Loading count={1} width={"150"} /> : data?.customerName ?? ''
        }];
        if (individual?.ssn) {
            infoHeaderData.push({
                title: 'Kennitala',
                value: insertDash(individual.ssn)
            });
        } else if (!data) {
            infoHeaderData.push({
                title: 'Kennitala',
                value: <Loading count={1} width={"150"} />
            });
        } else if (data?.customerSSN) {
            infoHeaderData.push({
                title: 'Kennitala',
                value: insertDash(data.customerSSN)
            });
        }
        //fæðingardagur only if no ssn
        else if (data === null) {
            infoHeaderData.push({
                title: 'Fæðingardagur',
                value: <Loading count={1} width={"150"} />
            }
            );
        } else if (data?.user?.dateOfBirth) {
            infoHeaderData.push({
                title: 'Fæðingardagur',
                value: getTableDate(data.user.dateOfBirth, 'DD/MM/YYYY', '.')
            });
        } else if (individual?.dateOfBirth) {
            infoHeaderData.push({
                title: 'Fæðingardagur',
                value: getTableDate(individual.dateOfBirth, 'DD/MM/YYYY', '.')
            });
        } else if (!data) {
            infoHeaderData.push({
                title: 'Fæðingardagur',
                value: <Loading count={1} width={"150"} />
            });
        }
        if (data?.user) {
            infoHeaderData.push({
                title: 'Póstfang',
                value: displayAddress(data.user.address, data.user.areaCode, data.user.region, data.user.country) || ''
            });
            return infoHeaderData;
        }
        return infoHeaderData;
    }, [data]);


    // Fetch risk score data (stageData)
    useEffect(() => {
        const fetchStageData = async () => {
            try {
                setStageData(null); // Set to loading state
                const response = await fetch(`/DueDiligence/GetRiskscores/${customerId}?isIndividual=true`);
                if (!response.ok) {
                    // Check for error HTTP status
                    if (response.status === 404) {
                        return;
                    } else if (response.status === 403) {
                        return;
                    }
                    throw new Error(`${await response.text()}`);
                }

                const data: RiskScoreDto[] = await response.json();
                const filteredData = data.filter(rs => {
                    return rs.id.toString() === riskscoreId.toString()
                })
                filteredData.length > 0 && setStageData(filteredData[0]);
            } catch (e) {
                if (e instanceof Error) {
                    Bugsnag.notify(e);
                    setStageData(e);
                }
            }
        }
        fetchStageData();
    }, []);

    useEffect(() => {
        setStageData(prev => {
            // Guard clause: only proceed if we have valid previous state data
            if (!prev || prev instanceof Error) {
                return prev;
            }

            // Guard clause: only update if riskScore exists and has a finished timestamp
            if (!riskScore || !riskScore.riskscoreFinishedTimestamp) {
                return prev;
            }

            // Only update if the risk assessment wasn't previously marked as finished
            // This prevents unnecessary state updates on subsequent renders
            if (!prev.riskscoreFinishedTimestamp) {
                return {
                    ...prev,
                    riskscoreFinishedTimestamp: riskScore.riskscoreFinishedTimestamp
                };
            }

            // No changes needed - risk assessment was already marked as finished
            return prev;
        });
    }, [riskScore]);

    const progressBarLoading = useMemo(() => {
        if (riskScore === null) return false;
        // If either data source is still loading (null) or hasn't started (undefined), show loading
        return stageData === null || stageData === undefined || data === null || data === undefined;
    }, [stageData, data]);

    const progressBarError = useMemo(() => {
        return stageData instanceof Error || data instanceof Error;
    }, [stageData, data]);

    const IndividualOverviewData = useMemo(() => {
        // Return empty array if data is not available or is an error
        if (data === undefined || data instanceof Error || data === null) {
            return [];
        }
        const individual = data.user;
        // Create the record object with individual overview information
        const recordToReturn: {
            rikisfang: string | undefined;
            rikisfangflag: boolean | undefined;
            busetuland: string | undefined;
            busetulandflag: boolean | undefined;

            pepStatus: boolean | undefined;
            pepData?: Result[];
            sanctionsStatus: boolean;
            sanctionsData?: PepResult[];
            hlutafelagapattaka?: string;
            ordsporsahætta?: string;
            ssn: string | null
        } = {
            // Get nationality from individual info (may be undefined)
            rikisfang: individual?.nationality,
            rikisfangflag: individual?.nationalityFlagged,
            //if address is flagged we can assume that address is a country
            busetuland: individual?.addressCountryChecked !== null || individual?.country !== null
                ? getCountryName(individual?.addressCountryChecked ?? individual?.country)
                : individual.addressChecked
                    ? individual.addressChecked
                    : undefined,

            busetulandflag: individual?.addressFlagged,
            // Check if person is politically exposed (PEP)
            pepStatus: data.pepReport?.results?.[0]?.connected,
            pepData: data.pepReport?.results,
            // Check if person has foreign PEP reports (sanctions)
            sanctionsStatus: data?.foreignPepReports?.[0]?.hasResults,
            sanctionsData: data?.foreignPepReports?.[0]?.mappedReport?.match || undefined,
            ssn: data.customerSSN
        };
        // Return as single-item array
        return [recordToReturn];
    }, [data, individual]);

    const renderBody = () => {
        if (data instanceof Error) {
            return <Alert type="error" headText="Villa við að sækja gögn um áreiðanleikakönnun." text={data.message} />
        }
        if (data === undefined) {
            return <Alert type="error" headText="Villa við að sækja gögn um áreiðanleikakönnun." />
        }
        return (<>
            {/* Header with individual info */}
            <InfoHeader noMargin data={infoHeaderData} />
            {progressBarError ? (
                // Show error alert if either fetch failed
                <Alert type="error" headText="Ekki tókst að sækja gögn" />
            ) : (
                // Show progress bar - it will handle its own loading state internally
                <KYCSurveyProgressBar
                    loading={progressBarLoading}
                    konnunHafin={stageData && !(stageData instanceof Error)
                        ? stageData.createdTimestamp
                        : undefined}
                    konnunSend={
                        // adding the backup so that if stageData is not available and the user for example sends the questionnaire 
                        // then the backup dateSent from answerLists will be updated right away and will be used. On refresh that stageData.dateSent will have updated.
                        stageData && !(stageData instanceof Error)
                            ? stageData.dateSent === null ? undefined : stageData.dateSent
                            : undefined}
                    ahaettumatSkrad={stageData && !(stageData instanceof Error)
                        ? stageData.riskscoreFinishedTimestamp
                        : undefined}
                    konnunSvarad={stageData && !(stageData instanceof Error)
                        ? stageData.answeredTimestamp
                        : undefined}
                    link={undefined}
                    type={"individual"} />
            )}
            {
                data?.answerLists && data.answerLists.length > 0 && <QuestionaireResults
                    riskScore={riskScore}
                    setRiskScoreModal_isOpen={setRiskScoreModal_isOpen}
                    setRiskScoreModal_data={setRiskScoreModal_data}
                    customerName={data.customerName} />
            }

            {/* Error state */}
            {data?.errorState && <Alert type="error" headText="Villa" text={data?.errorMessage ?? ""} />}

            {/* Individual overview table */}
            {
                !data?.errorState &&
                <DisplayBox>
                    {
                        data === null ?
                            <Loading width={"100%"} />
                            : <Table showEmptyTableText data={IndividualOverviewData} columns={[{
                                title: AthaettusomRikiTooltipColumnHeader("Ríkisfang"),
                                renderCell: ({ rikisfang, rikisfangflag }) => RenderNationalityWithTooltip(rikisfang, rikisfangflag),
                                textAlign: 'left'
                            }, {
                                title: AthaettusomRikiTooltipColumnHeader("Búseta"),
                                renderCell: ({ busetuland, busetulandflag }) => RenderNationalityWithTooltip(busetuland, busetulandflag),
                                textAlign: 'left'
                            }, {
                                title: StjornmalalegTengslPEPTooltipColumnHeader,
                                renderCell: ({ pepStatus, pepData }) => pepStatus !== undefined &&
                                    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: '3px', width: 'fit-content' }}>
                                        <Label labelType={pepStatus ? "yes" : "no"} size="lg" text={pepStatus ? "Já" : "Nei"} />
                                        {pepStatus &&
                                            <Button
                                                size="sm"
                                                buttonType="secondary"
                                                onClick={() => {
                                                    setShowPepModal({ show: true, data: sortEntries(pepData ? pepData : null) });
                                                }}
                                            >
                                                Sækja
                                            </Button>
                                        }
                                    </div>,
                                textAlign: 'left'
                            }, {
                                title: AlthjodlegirValistarTooltipColumnHeader,
                                renderCell: ({ sanctionsStatus, sanctionsData }) => sanctionsStatus !== undefined &&
                                    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: '3px', width: 'fit-content' }}>
                                        <Label labelType={sanctionsStatus ? "yes" : "no"} size="lg" text={sanctionsStatus ? "Já" : "Nei"} />
                                        {sanctionsStatus &&
                                            <Button
                                                size="sm"
                                                buttonType="secondary"
                                                onClick={() => {
                                                    setForeignPepModal_data(sanctionsData);
                                                    setForeignPepModal_isOpen(true);
                                                }}
                                            >
                                                Sækja
                                            </Button>
                                        }
                                    </div>,
                                textAlign: 'left'
                            }, {
                                title: 'Hlutafélagaþátttaka',
                                renderCell: ({ ssn }) => <Button disabled={ssn === null} onClick={() => handleBuyMembership(ssn as string)} size="sm" buttonType="secondary">Skoða</Button>,
                                textAlign: 'center'
                            }]} />
                    }
                    <div style={{ padding: '3px', width: 'fit-content', marginTop: '20px' }}>
                        <Button size="lg" disabled={disableSendNotificationButton} onClick={async () => {
                            setDisableSendNotificationButton(true);
                            setEditIndividualModal_isOpen(true);
                            setDisableSendNotificationButton(false);
                        }}>{!riskScore?.dateSent ? 'Senda könnun' : 'Senda ítrekun'}</Button>
                        {riskScore?.dateSent && <span style={{ marginLeft: '20px' }} className="paragraph--bold">Könnun send {getTableDate(riskScore?.dateSent, 'DD/MM/YYYY', '.')}</span>}
                    </div>
                </DisplayBox>
            }

            {/* Individual answers */}
            {data?.answerLists && data.answerLists.length > 0 && <IndividualAnswers
                data={data}
                answerListIndex={answerListIndex}
                questionListVersion={data.answerLists[answerListIndex].questionListVersion as availableVersions}
                questionListType={data.answerLists[answerListIndex].questionListType as QuestionListType}
            />}

            {/* File upload section */}
            {
                data?.answerLists[answerListIndex] && data.answerLists[answerListIndex].answerlistId &&
                <DisplayBox id="file-upload" title="Fylgiskjöl">
                    <p className="paragraph-small--bold instruction-text">Settu inn allt að 10 skjöl:</p>
                    <FileUploadDropzone initialFiles={data.answerLists[answerListIndex].serviceCompanyFiles} answerListId={data.answerLists[answerListIndex].answerlistId} />
                </DisplayBox>
            }
            {/* logs */}
            <Logs data={data} />
        </>)
    }
    return (<>
        <div className="KCL_DueDiligence-ViewCompanyAnswers">
            <KYCNavBar />
            <div className="ViewCompanyAnswers__content">
                <div className="shell page_content">
                    <div className="section__header">
                        <div className="section__header--left">
                            <GoBackLink />
                            <h1 title="Áreiðanleikakönnun">
                                Áreiðanleikakönnun
                            </h1>
                        </div>
                    </div>
                    {renderBody()}
                </div >
            </div>
            {/* Risk Score Modal */}
            <RiskScoreModal
                show={riskScoreModal_isOpen}
                close={() => setRiskScoreModal_isOpen(false)}
                data={riskScoreModal_data}
                setToastShow={setToastShow}
                setToastType={setToastType}
                setToastMessage={setToastMessage}
                riskscore={riskScore}
                setRiskscore={setRiskScore}
            />
            {/* Edit Individual Modal */}
            <EditIndividualModal
                show={editIndividualModal_isOpen}
                employees={employees ?? []}
                close={() => setEditIndividualModal_isOpen(false)}
                data={mergedIndividual}
                setToastShow={setToastShow}
                setToastType={setToastType}
                setToastMessage={setToastMessage}
                riskScore={riskScore}
                setRiskScore={setRiskScore}
                sendNotification={true}
            />
            <ForeignPepModal
                show={foreignPepModal_isOpen}
                close={() => setForeignPepModal_isOpen(false)}
                data={foreignPepModal_data}
            />
            <PepWatchPEPConnectionModal
                show={showPepModal.show}
                close={() => setShowPepModal({ show: false })}
                fetchedData={showPepModal.data}
            />
        </div >

        <ToastContainer className="toastContainer">
            <Toast onClose={() => setToastShow(false)} autohide delay={TOAST_DELAY} show={toastShow}>
                <Alert type={toastType} headText={toastMessage} />
            </Toast>
        </ToastContainer>
    </>
    )
};

export default ViewIndividualAnswers;

export const TBATooltipWrapper: React.FC<{ text: string | JSX.Element }> = ({ text }) => {
    return <Tooltip noUnderline text={text} tooltip={"Væntanlegt"} />
}